'use client';

import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import useSWR from 'swr';
import { getPublicPollingSettings, type PollingSettings } from '@/lib/api-client';
import { isDuringMarketHours } from '@/lib/marketHours';

/** Single dashboard refresh broadcaster. Increments a counter every
 *  `pollingSettings.dashboardRefreshMinutes` during US regular trading hours.
 *  Any tile that subscribes and lists `tick` in its fetch effect's deps will
 *  re-run its loader on each tick.
 *
 *  Polling is suspended outside RTH (issue #104 open-question 1) and resumes
 *  at the next market open. No user gate — anonymous and logged-in viewers
 *  both tick (open-question 2).
 *
 *  The provider runs a single setInterval so every tile ticks on the exact
 *  same moment (vs. N drifting per-tile timers). `refreshNow()` stays
 *  exposed for a future manual refresh button.
 */
type TickContextValue = { tick: number; refreshNow: () => void };

const TickContext = createContext<TickContextValue>({ tick: 0, refreshNow: () => {} });

export function DashboardTickProvider({ children }: { children: React.ReactNode }) {
  const { data } = useSWR<PollingSettings>(
    '/admin/public/polling',
    () => getPublicPollingSettings(),
    { revalidateOnFocus: false, refreshInterval: 0, dedupingInterval: 60_000 },
  );
  const intervalMinutes = data?.dashboardRefreshMinutes ?? 15;

  const [tick, setTick] = useState(0);
  // Keep the latest interval in a ref so the scheduler effect doesn't
  // re-subscribe every time the admin bumps the setting.
  const intervalRef = useRef(intervalMinutes);
  intervalRef.current = intervalMinutes;

  useEffect(() => {
    let lastTickAt = Date.now();
    const id = setInterval(() => {
      if (!isDuringMarketHours()) return;
      const now = Date.now();
      if (now - lastTickAt >= intervalRef.current * 60_000) {
        lastTickAt = now;
        setTick((t) => t + 1);
      }
    }, 30_000);
    return () => clearInterval(id);
  }, []);

  const refreshNow = useCallback(() => setTick((t) => t + 1), []);

  return React.createElement(TickContext.Provider, { value: { tick, refreshNow } }, children);
}

export function useDashboardTick(): TickContextValue {
  return useContext(TickContext);
}
